import { Line2D, LineSegment2D, minimaFuzzy, Point2D, Polygon3D } from '@paragon/geometry-library';
import { BoundedPlaneShape, DisplayablePlaneWithCalculatedGeometry } from '@paragon/layout';
import { notNullish } from '@paragon/utilities';

/**
 * Roof planes are often defined by an infinite geometry, and even when they are not, the defining geometry segment
 *   is often not an ideal visual representation of how the user perceives the roof plane. This function attempts to
 *   infer a more appropriate segment(s) to use for a roof plane's definition visual.
 *
 * Returns null if there is nothing to base a finite visual on.
 */
export function createVisualDefiningSegment(
  plane: DisplayablePlaneWithCalculatedGeometry,
  calculatedBounds?: BoundedPlaneShape,
): LineSegment2D[] | null {
  const planeGeometry = plane.calculatedGeometry;
  const definingLine = planeGeometry.line;

  const boundsPolygons =
    calculatedBounds && 'polygons' in calculatedBounds ? calculatedBounds.polygons : calculatedBounds;

  let definingSegments;
  if (boundsPolygons?.length) {
    const featureSets = getDefiningFeatureSets(definingLine, boundsPolygons);
    const validSegmentses = featureSets
      .map((features) => createHorizontalSegmentsSpanning(definingLine, features))
      .filter(notNullish)
      .filter((segments) => segments.length !== 0);

    if (validSegmentses.length > 0) {
      // Take the ideal case, and if its segments are shorter
      //   than the minimum visual length, extend each in place so a short-but-correct base edge stays centered on the
      //   actual bounds rather than being thrown away in favor of the vertex-span fallback.
      definingSegments = validSegmentses[0].map(ensureMinimumLength);
    }
  }

  if (!definingSegments) {
    if (planeGeometry.segment) {
      definingSegments = [ensureMinimumLength(planeGeometry.segment)]; // fall back to the defining segment if possible
    } else {
      return null; // otherwise we can't determine a segment
    }
  }

  return definingSegments.map((segment) => segment.orientWith(definingLine.direction));
}

// To determine the extent of a definition visual, we use an algorithm based on creating a segment that spans a number
//   of features (vertices, edges) that must be covered by the visual. There are a number of different cases under
//   which we can find these features, but they all take one of these two forms.
type DefiningFeatures = { vertices: readonly Point2D[] } | { edges: readonly LineSegment2D[] };

// Returns an array containing different possible sets of defining features that could be used, ordered by desirability.
function getDefiningFeatureSets(definingLine: Line2D, calculatedBounds: readonly Polygon3D[]): DefiningFeatures[] {
  return [
    getDefiningFeaturesForIdealCase(definingLine, calculatedBounds),
    getDefiningFeaturesForVerticalPlane(calculatedBounds),
    getDefiningFeaturesForFallbackCase(calculatedBounds),
  ].filter(notNullish);
}

// The ideal definition visual spans the intersection (even if not continuous) of the roof face and the plane's defining
//   line.
function getDefiningFeaturesForIdealCase(
  definingLine: Line2D,
  calculatedBounds: readonly Polygon3D[],
): DefiningFeatures | null {
  const boundPolygon2D = calculatedBounds.map((bounds) => bounds.projectTo2D()).filter(notNullish);
  return boundPolygon2D.length
    ? { edges: boundPolygon2D.flatMap((bounds) => bounds.findSegmentIntersectionsWithLine(definingLine)) }
    : null;
}

// Special cases for vertical planes only. These planes will not produce a 2D polygon, so they will always fail the
//   "ideal" case.
function getDefiningFeaturesForVerticalPlane(calculatedBounds: readonly Polygon3D[]): DefiningFeatures | null {
  if (!calculatedBounds.every((bounds) => bounds.isVertical())) return null;

  const lowestVertices = minimaFuzzy(
    calculatedBounds.flatMap(({ vertices }) => vertices),
    (vertex) => vertex.z,
  );
  if (lowestVertices.length >= 2) {
    return { vertices: lowestVertices.map((vertex) => vertex.projectTo2D()) };
  }

  const valid2dEdges = calculatedBounds.flatMap(({ edges }) => edges).filter((edge) => edge.projectTo2D() != null);
  const lowestEdges = minimaFuzzy(valid2dEdges, (edge) => edge.midpoint.z);
  if (lowestEdges.length >= 1) {
    return { edges: lowestEdges.map((edge) => edge.projectTo2D()).filter(notNullish) };
  }

  return null;
}

// Basing the visual on the entire bounds of the roof face is a last resort, but it is still usually better than falling
//   back to the defining segment itself.
function getDefiningFeaturesForFallbackCase(calculatedBounds: readonly Polygon3D[]): DefiningFeatures | null {
  return { vertices: calculatedBounds.flatMap(({ vertices }) => vertices).map((v) => v.projectTo2D()) };
}

/**
 * Creates `LineSegment2D`s that are collinear with the `definingLine` but span the given `DefiningFeatures`.
 *
 * @param definingLine     The line that defines the roof plane.
 * @param definingFeatures The features that the visual should span.
 */
function createHorizontalSegmentsSpanning(definingLine: Line2D, definingFeatures: DefiningFeatures) {
  if ('edges' in definingFeatures) return definingFeatures.edges;

  if (definingFeatures.vertices.length < 2) return null;

  const vertexDistances = definingFeatures.vertices.map((point) => point.projectedSignedDistanceAlong(definingLine));

  const minDistance = Math.min(...vertexDistances);
  const maxDistance = Math.max(...vertexDistances);

  const minPoint = definingLine.getPointAlongLine(minDistance);
  const maxPoint = definingLine.getPointAlongLine(maxDistance);

  return !minPoint.equals(maxPoint) ? [new LineSegment2D(minPoint, maxPoint)] : null;
}

// Roof planes must be big enough to be visible within the scope of a layout, and long enough for at least one tooth.
//   There's no harm in a visual that is longer than the definition segment, as all roof planes are actually infinite.
const MINIMUM_VISUAL_SEGMENT_LENGTH = 36;

function ensureMinimumLength(segment: LineSegment2D) {
  if (segment.length >= MINIMUM_VISUAL_SEGMENT_LENGTH) return segment;

  // Build the line off the segment's midpoint so the extended visual is centered on the original edge instead of biased
  //   toward its start endpoint.
  const line = new Line2D(segment.midpoint, segment.direction);
  return new LineSegment2D(
    line.reverse().getPointAlongLine(MINIMUM_VISUAL_SEGMENT_LENGTH / 2),
    line.getPointAlongLine(MINIMUM_VISUAL_SEGMENT_LENGTH / 2),
  );
}
